import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../api";
import { BrutalButton, StockBadge } from "../components/Brutal";
import RevenueWidget from "../components/RevenueWidget";
import ExpensesWidget from "../components/ExpensesWidget";
import StatCard from "../components/StatCard";
import ReceivablesCard from "../components/ReceivablesCard";
import BackfillSaleDialog from "../components/BackfillSaleDialog";
import { MigrationReportCard } from "../components/MigrationReportCard";
import { LedgerHealthCard } from "../components/LedgerHealthCard";
import { EmailLogCard } from "../components/EmailLogCard";
import { RegionBlocks } from "../components/RegionBlocks";
import { Warning, Package, Users, ArrowRight, TrendUp, PaperPlaneTilt, X, Check, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../AuthContext";

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { isOwner, isSuperOwner, user } = useAuth();
  const [data, setData] = useState(null);
  const [requests, setRequests] = useState([]);
  const [discrepancies, setDiscrepancies] = useState([]);
  const [reconciling, setReconciling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revenueTick, setRevenueTick] = useState(0);
  const [showBackfill, setShowBackfill] = useState(false);
  const [regions, setRegions] = useState([]); // v405.11 region hosting blocks

  const load = async () => {
    try {
      const [overview, reqs, disc, regs] = await Promise.all([
        api.get("/overview"),
        api.get("/restock-requests", { params: { status: "pending" } }),
        api.get("/overview/discrepancies"),
        api.get("/overview/regions").catch(() => ({ data: [] })),
      ]);
      setData(overview.data);
      setRequests(reqs.data);
      setDiscrepancies(disc.data || []);
      setRegions(regs.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const dismissRequest = async (id) => {
    try {
      await api.post(`/restock-requests/${id}/dismiss`);
      setRequests((rs) => rs.filter((r) => r.id !== id));
      toast.success(t("restock.dismissed"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const fulfillRequest = async (req) => {
    try {
      await api.post(`/restock-requests/${req.id}/fulfill`, {
        assign_stock: !!req.quantity,
        quantity: req.quantity || null,
      });
      setRequests((rs) => rs.filter((r) => r.id !== req.id));
      toast.success(req.quantity ? t("restock.sent_to", { n: req.quantity, name: req.distributor_name }) : t("restock.fulfilled"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reconcileDiscrepancy = async (distributorId) => {
    setReconciling(distributorId);
    try {
      const res = await api.post(`/overview/discrepancies/${distributorId}/reconcile`);
      toast.success(t("dashboard.discrepancy_reconciled", { n: res.data.total_unaccounted }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReconciling(null);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 8000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return <p className="text-sm text-zinc-500">{t("common.loading")}</p>;
  }

  const ownLow = data?.own_low_stock_items || [];
  const distLow = data?.distributor_low_stock_items || [];
  const totalLow = ownLow.length + distLow.length;
  // Bumped by ReceivablesCard whenever a receivable is paid/unpaid so the
  // RevenueWidget refetches immediately (the captured sale_price flows into
  // /api/revenue's aggregation).
  const bumpRevenue = () => setRevenueTick((n) => n + 1);

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t("dashboard.title")}</h1>
          <p className="text-sm sm:text-base text-zinc-600 mt-1">
            {t("dashboard.subtitle")}
          </p>
        </div>
        <div className="flex gap-2 sm:gap-3 sm:flex-wrap -mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto sm:overflow-visible scrollbar-hide" data-testid="dashboard-action-row">
          {(isOwner || isSuperOwner) && (
            <>
              <Link to="/inventory/intake" className="shrink-0">
                <BrutalButton data-testid="dashboard-intake-btn">{t("dashboard.scan_csv_intake")}</BrutalButton>
              </Link>
              <Link to="/inventory/new" className="shrink-0">
                <BrutalButton variant="secondary" data-testid="dashboard-add-product-btn">{t("dashboard.add_product")}</BrutalButton>
              </Link>
            </>
          )}
          {isSuperOwner && (
            <BrutalButton
              variant="secondary"
              onClick={() => setShowBackfill(true)}
              data-testid="open-backfill-btn"
              title={t("backfill.button_tooltip")}
              className="shrink-0"
            >
              <ArrowsClockwise size={16} weight="bold" /> {t("backfill.button")}
            </BrutalButton>
          )}
          <Link to="/distributors" className="shrink-0">
            <BrutalButton variant="secondary" data-testid="dashboard-manage-distributors-btn">
              {t("dashboard.manage_distributors")}
            </BrutalButton>
          </Link>
        </div>
      </div>

      {totalLow > 0 && (
        <div
          className="border border-red-200 bg-red-50 text-red-900 rounded-xl p-4 sm:p-5 flex items-start gap-3"
          data-testid="low-stock-banner"
        >
          <Warning size={22} weight="fill" className="text-red-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base">{t("dashboard.reorder_needed")}</h3>
            <p className="text-sm text-red-800 mt-0.5">
              {t("dashboard.below_40", { count: totalLow })}
            </p>
          </div>
        </div>
      )}

      <RegionBlocks regions={regions} />

      {discrepancies.length > 0 && (
        <section
          className="border border-amber-200 bg-amber-50 rounded-xl p-4 sm:p-5"
          data-testid="discrepancy-report"
        >
          <div className="flex items-start gap-3">
            <Warning size={22} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base text-amber-950">{t("dashboard.discrepancy_title")}</h3>
              <p className="text-sm text-amber-800 mt-0.5 mb-3">{t("dashboard.discrepancy_subtitle")}</p>
              <div className="space-y-2">
                {discrepancies.map((d) => (
                  <Link
                    key={d.distributor_id}
                    to={`/distributors/${d.distributor_id}`}
                    className="flex items-center justify-between gap-3 bg-white border border-amber-200 rounded-lg px-3 py-2 hover:border-amber-400 transition-colors"
                    data-testid={`discrepancy-row-${d.distributor_id}`}
                  >
                    <div className="min-w-0">
                      <span className="font-medium text-sm text-zinc-900 truncate block">{d.distributor_name || d.distributor_email}</span>
                      <span className="text-xs text-zinc-500">
                        {t("dashboard.discrepancy_meta", {
                          count: d.return_count,
                          date: d.last_return_at ? new Date(d.last_return_at).toLocaleDateString() : "—",
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold">
                        {t("dashboard.discrepancy_units", { n: d.total_unaccounted })}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          reconcileDiscrepancy(d.distributor_id);
                        }}
                        disabled={reconciling === d.distributor_id}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition-colors disabled:opacity-60"
                        title={t("dashboard.discrepancy_reconcile_tooltip")}
                        data-testid={`reconcile-btn-${d.distributor_id}`}
                      >
                        <Check size={12} weight="bold" />
                        {reconciling === d.distributor_id
                          ? t("dashboard.discrepancy_reconciling")
                          : t("dashboard.discrepancy_reconcile")}
                      </button>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {requests.length > 0 && (
        <section data-testid="pending-requests-section">
          <h2 className="text-lg sm:text-xl font-semibold tracking-tight mb-3 flex items-center gap-2 text-zinc-950">
            <PaperPlaneTilt size={18} weight="bold" />
            {t("dashboard.restock_requests")}
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-zinc-100 text-zinc-700 text-xs font-medium rounded-full">
              {requests.length}
            </span>
          </h2>
          <div className="space-y-3">
            {requests.map((r) => (
              <div
                key={r.id}
                data-testid={`request-row-${r.id}`}
                className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-zinc-950 truncate">{r.product_name}</span>
                    {r.new_product && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-[#fbf6e7] text-[#8a6a1c] border border-[#c9a24a] rounded-full" data-testid={`new-product-flag-${r.id}`}>
                        {t("dashboard.new_flag")}
                      </span>
                    )}
                    {r.auto && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded-full">
                        {t("dashboard.auto_flag")}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-zinc-500 mt-0.5">
                    {t("dashboard.from")} {r.distributor_name}
                    {r.quantity ? ` · ${t("dashboard.wants_quantity", { quantity: r.quantity })}` : ""}
                  </div>
                  {r.note && r.note !== "Auto-flagged: stock crossed below 40%" && (
                    <div className="text-sm italic text-zinc-600 mt-1">“{r.note}”</div>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <BrutalButton
                    size="sm"
                    variant="secondary"
                    onClick={() => dismissRequest(r.id)}
                    data-testid={`dismiss-${r.id}`}
                  >
                    <X size={14} weight="bold" /> {t("dashboard.dismiss")}
                  </BrutalButton>
                  <BrutalButton
                    size="sm"
                    onClick={() => fulfillRequest(r)}
                    data-testid={`fulfill-${r.id}`}
                  >
                    <Check size={14} weight="bold" />
                    {r.quantity ? t("dashboard.send_n", { n: r.quantity }) : t("dashboard.mark_done")}
                  </BrutalButton>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard
          to="/inventory"
          label={isOwner ? t("dashboard.stat_my_products") : t("dashboard.stat_master_inventory")}
          value={data.own_product_count}
          icon={Package}
        />
        <StatCard
          to="/distributors"
          label={t("dashboard.stat_distributors")}
          value={data.distributor_count}
          icon={Users}
        />
        <StatCard
          to="/inventory"
          label={t("dashboard.stat_my_low_stock")}
          value={data.own_low_stock_count}
          icon={Warning}
          warn={data.own_low_stock_count > 0}
        />
        <StatCard
          to="/distributors"
          label={t("dashboard.stat_distributor_low_stock")}
          value={data.distributor_low_stock_count}
          icon={TrendUp}
          warn={data.distributor_low_stock_count > 0}
        />
      </div>

      {(data.in_stock_total !== undefined) && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="inventory-totals">
          <StatCard
            testId="stat-in-stock"
            to="/inventory"
            label={t("dashboard.stat_in_stock")}
            value={data.in_stock_total}
            icon={Package}
          />
          <StatCard
            testId="stat-on-consignment"
            to="/distributors"
            label={t("dashboard.stat_on_consignment")}
            value={data.on_consignment_total}
            icon={PaperPlaneTilt}
          />
          <StatCard
            testId="stat-sold"
            to="/movement-log"
            label={t("dashboard.stat_sold")}
            value={data.sold_total}
            icon={Check}
          />
          <StatCard
            testId="stat-samples"
            to="/movement-log"
            label={t("dashboard.stat_samples", { cost: data.samples_cost ? ` ($${Number(data.samples_cost).toLocaleString()})` : "" })}
            value={data.samples_total || 0}
            icon={Users}
          />
        </div>
      )}

      {data.stock_distribution && <StockDistributionCard dist={data.stock_distribution} t={t} />}

      {(["owner", "admin", "cfo"].includes(user?.role) || isSuperOwner) && <LedgerHealthCard />}
      {(["owner", "admin"].includes(user?.role) || isSuperOwner) && <EmailLogCard />}

      {(isOwner || isSuperOwner) && <ReceivablesCard t={t} onPaymentChange={bumpRevenue} />}

      {(isOwner || isSuperOwner) && <RevenueWidget refreshTick={revenueTick} />}

      {(isOwner || isSuperOwner) && <ExpensesWidget refreshTick={revenueTick} />}

      <section>
        <h2 className="text-lg sm:text-xl font-semibold tracking-tight mb-3 text-zinc-950">
          {t("dashboard.my_low_stock_items")}
        </h2>
        {ownLow.length === 0 ? (
          <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 text-sm text-zinc-600">
            {t("dashboard.nothing_below_40")}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {ownLow.map((p) => (
              <LowStockRow key={p.id} p={p} />
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-lg sm:text-xl font-semibold tracking-tight mb-3 text-zinc-950">
          {t("dashboard.distributor_low_stock")}
        </h2>
        {distLow.length === 0 ? (
          <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 text-sm text-zinc-600">
            {t("dashboard.no_distributor_below_40")}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {distLow.map((p) => (
              <LowStockRow key={p.id + p.distributor_id} p={p} dist />
            ))}
          </div>
        )}
      </section>

      {isSuperOwner && <MigrationReportCard />}

      {showBackfill && (
        <BackfillSaleDialog
          onClose={() => setShowBackfill(false)}
          onApplied={() => {
            bumpRevenue();
            load();
          }}
        />
      )}
    </div>
  );
}

function StockDistributionCard({ dist, t }) {
  const segs = [
    { key: "master", qty: dist.master_units || 0, cls: "bg-[#c9a24a]" },
    { key: "partners", qty: dist.partner_units || 0, cls: "bg-[#e4cf94]" },
    { key: "distributors", qty: dist.distributor_units || 0, cls: "bg-zinc-800" },
  ];
  const total = segs.reduce((s, x) => s + x.qty, 0);
  return (
    <section className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm" data-testid="stock-distribution-card">
      <div className="flex items-baseline justify-between gap-2 mb-3 flex-wrap">
        <h2 className="text-lg font-semibold tracking-tight">{t("dashboard.stock_distribution")}</h2>
        <span className="text-xs text-zinc-500 tabular-nums">
          {t("dashboard.stock_distribution_total", { n: total.toLocaleString() })}
        </span>
      </div>
      {total === 0 ? (
        <p className="text-sm text-zinc-500">{t("dashboard.stock_distribution_empty")}</p>
      ) : (
        <>
          <div className="h-3 flex rounded-full overflow-hidden border border-zinc-200 bg-zinc-100">
            {segs.map(
              (s) =>
                s.qty > 0 && (
                  <div key={s.key} className={s.cls} style={{ width: `${(s.qty / total) * 100}%` }} />
                )
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
            {segs.map((s) => (
              <div key={s.key} className="flex items-center gap-1.5 text-sm" data-testid={`stock-dist-${s.key}`}>
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${s.cls}`} />
                <span className="text-zinc-600">{t(`dashboard.stock_dist_${s.key}`)}</span>
                <span className="font-semibold tabular-nums">{s.qty.toLocaleString()}</span>
                <span className="text-xs text-zinc-400 tabular-nums">
                  ({Math.round((s.qty / total) * 100)}%)
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function LowStockRow({ p, dist }) {
  const { t } = useTranslation();
  const percent = p.starting_stock > 0 ? (p.current_stock / p.starting_stock) * 100 : 0;
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="font-semibold text-zinc-950 truncate">{p.name}</div>
        {dist && (
          <div className="text-xs text-zinc-500 mt-0.5">
            @ {p.distributor_name}
          </div>
        )}
        <div className="text-sm text-zinc-600 mt-1">
          {p.current_stock} / {p.starting_stock} {p.unit || t("common.units")}
        </div>
      </div>
      <div className="flex flex-col items-end gap-2 shrink-0">
        <StockBadge percent={percent} />
        {dist && (
          <Link
            to={`/distributors/${p.distributor_id}`}
            className="text-xs font-medium text-[#8a6a1c] hover:underline flex items-center gap-1"
          >
            {t("dashboard.view")} <ArrowRight size={12} weight="bold" />
          </Link>
        )}
      </div>
    </div>
  );
}

